import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Prompt, PromptForm } from "@/shared/ui";
import { EmptyState, Pagination } from "@/shared/components";
import { useLazyMount } from "@/shared/hooks";
import { proxyS3Url } from "@/shared/utils/proxyS3Url";
import {
  useAttachedDocumentsListPage,
  useAttachedDocumentsModals,
  useAttachedDocumentsMutations,
  useAttachedDocumentsViewer,
} from "../../hooks";
import { AttachedDocumentsHeader, AttachedDocumentsFilter, AttachedDocumentsPageSkeleton } from "./components";

const AttachedDocumentsCardsView = lazy(() => import("./components/AttachedDocumentsCardsView"));
const AttachedDocumentCreateModal = lazy(() => import("./components/AttachedDocumentCreateModal"));
const AttachedDocumentEditModal = lazy(() => import("./components/AttachedDocumentEditModal"));
const AttachedDocumentDetailsModal = lazy(() => import("./components/AttachedDocumentDetailsModal"));
const PdfViewer = lazy(() => import("@/shared/ui/PdfViewer"));
const ImageViewer = lazy(() => import("@/shared/ui/ImageViewer"));

export default function AttachedDocumentsPage() {
  const { t } = useTranslation("AttachedDocumentsPage");

  const {
    documents,
    isLoading,
    isError,
    search,
    setSearch,
    page,
    setPage,
    totalPages,
    totalCount,
    fromItem,
    toItem,
    hasActiveFilters,
    typeFilter,
    setTypeFilter,
    dateRange,
    setDateRange,
    sortBy,
    setSortBy,
    handleResetFilters,
  } = useAttachedDocumentsListPage();

  const {
    createModal,
    setCreateModal,
    editModal,
    setEditModal,
    detailModal,
    setDetailModal,
    confirmDelete,
    setConfirmDelete,
    prompt,
    setPrompt,
  } = useAttachedDocumentsModals();

  const {
    uploading,
    deletingId,
    isDeleting,
    handleUpload,
    handleUpdate,
    handleDelete,
  } = useAttachedDocumentsMutations(setPrompt);

  const { viewer, loadingId: viewerLoadingId, openViewer, closeViewer } = useAttachedDocumentsViewer(setPrompt, t);

  const createModalWasOpened = useLazyMount(createModal);

  const isAnyModalOpen =
    createModal || editModal !== null || detailModal !== null || confirmDelete !== null || viewer !== null;

  async function onDeleteConfirm(id: number) {
    await handleDelete(id);
    setConfirmDelete(null);
  }

  if (isLoading && !documents.length) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <AttachedDocumentsHeader />
          <AttachedDocumentsPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <AttachedDocumentsHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.errorTitle")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <AttachedDocumentsHeader sortBy={sortBy} onSortChange={setSortBy} />

        <div className="radius-3xl surface-base-fill p-7">
          <AttachedDocumentsFilter
            search={search}
            onSearchChange={setSearch}
            onUpload={() => setCreateModal(true)}
            typeFilter={typeFilter}
            onTypeChange={setTypeFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            hasActiveFilters={hasActiveFilters}
            onResetFilters={handleResetFilters}
          />

          {documents.length === 0 ? (
            <EmptyState
              hasActiveFilters={hasActiveFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noDocuments")}
              noDataHint={t("table.noDocumentsHint")}
            />
          ) : (
            <>
              <Suspense fallback={<AttachedDocumentsPageSkeleton cardsOnly />}>
                <AttachedDocumentsCardsView
                  records={documents}
                  onOpen={openViewer}
                  onDetails={setDetailModal}
                  onEdit={setEditModal}
                  onDelete={setConfirmDelete}
                  viewerLoadingId={viewerLoadingId}
                  deletingId={deletingId}
                  isAnyModalOpen={isAnyModalOpen}
                  setPrompt={setPrompt}
                />
              </Suspense>

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                total={totalCount}
                fromItem={fromItem}
                toItem={toItem}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </section>

      {createModalWasOpened && (
        <Suspense fallback={null}>
          <AttachedDocumentCreateModal
            hidden={!createModal}
            onClose={() => setCreateModal(false)}
            onConfirm={handleUpload}
            isLoading={uploading}
          />
        </Suspense>
      )}

      {editModal && (
        <Suspense fallback={null}>
          <AttachedDocumentEditModal
            documentId={editModal.id}
            onClose={() => setEditModal(null)}
            onConfirm={handleUpdate}
            isLoading={false}
          />
        </Suspense>
      )}

      {detailModal && (
        <Suspense fallback={null}>
          <AttachedDocumentDetailsModal
            documentId={detailModal.id}
            onClose={() => setDetailModal(null)}
          />
        </Suspense>
      )}

      {viewer && viewer.type === "pdf" && (
        <Suspense fallback={null}>
          <PdfViewer fileUrl={viewer.fileUrl} fileName={viewer.fileName} onClose={closeViewer} />
        </Suspense>
      )}

      {viewer && viewer.type === "image" && (
        <Suspense fallback={null}>
          <ImageViewer imageUrl={proxyS3Url(viewer.fileUrl)} fileName={viewer.fileName} onClose={closeViewer} />
        </Suspense>
      )}

      {confirmDelete !== null && (
        <PromptForm
          title={t("confirm.deleteTitle")}
          text={t("confirm.deleteText")}
          variant="error"
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => onDeleteConfirm(confirmDelete)}
          isLoading={isDeleting}
          confirmText={t("confirm.deleteConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="AttachedDocumentsPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="AttachedDocumentsPage"
        />
      )}
    </>
  );
}
